import * as React from "react";
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useMIS } from "../MISContext";
import { getPlantMis } from "../../../../../../api/misApi";
import { useParams } from "react-router-dom";

export default function ProfitLossTab() {
  const { plantId } = useParams();
  const { invoicesRows, totalAvgProgress } = useMIS();
  const [projectCost, setProjectCost] = React.useState(0);
  const [invoiceAmount, setInvoiceAmount] = React.useState(0);

  // Load project cost and calculate invoice amount from MIS data
  React.useEffect(() => {
    const loadMisData = async () => {
      if (!plantId) return;
      try {
        const misData = await getPlantMis(plantId);
        const invoicesData = misData?.data?.data?.invoices;
        
        console.log("=== Profit & Loss - Retrieved MIS Data ===");
        console.log("Invoices Data:", invoicesData);
        
        if (invoicesData) {
          // Get project cost
          if (invoicesData.projectCost !== undefined) {
            setProjectCost(Number(invoicesData.projectCost) || 0);
          }
          
          // Calculate total invoice amount from all tasks
          let totalInvoiceAmount = 0;
          ["engineering", "procurement", "fabrication"].forEach((task) => {
            const taskData = invoicesData[task];
            if (taskData) {
              // Sum all invoice amounts for this task
              Object.keys(taskData).forEach((key) => {
                const match = key.match(/^inv(\d+)$/);
                if (match && taskData[key]?.amount) {
                  totalInvoiceAmount += Number(taskData[key].amount) || 0;
                }
              });
            }
          });
          setInvoiceAmount(totalInvoiceAmount);
        }
        
        console.log("Project Cost:", projectCost);
        console.log("Invoice Amount:", invoiceAmount);
        console.log("==========================================");
      } catch (error) {
        console.error("Failed to load MIS data for Profit & Loss:", error);
      }
    };
    loadMisData();
  }, [plantId]);

  // Calculate progress from invoices rows (average of all tasks)
  const progress = React.useMemo(() => {
    if (!invoicesRows || invoicesRows.length === 0) return 80;
    const totalProgress = invoicesRows.reduce(
      (sum, row) => sum + (Number(row.progress) || 0),
      0
    );
    return totalProgress / invoicesRows.length;
  }, [invoicesRows]);

  // Calculate derived values
  const earnedValues = React.useMemo(() => {
    return (projectCost * totalAvgProgress) / 100;
  }, [projectCost, progress]);

  const difference = React.useMemo(() => {
    return earnedValues - invoiceAmount;
  }, [earnedValues, invoiceAmount]);

  // Format number with commas
  const formatNumber = (num) => {
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const rows = [
    { no: 1, description: "Project cost", value: formatNumber(projectCost) },
    { no: 2, description: "Amount % Progress", value: `${totalAvgProgress}%` },
    { no: 3, description: "Earned values", value: formatNumber(earnedValues) },
    {
      no: 4,
      description: "Invoice amount",
      value: formatNumber(invoiceAmount),
    },
    { no: 5, description: "Difference", value: formatNumber(difference) },
  ];

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-start" }}>
      <Paper 
        elevation={0} 
        sx={{ 
          width: "50%", 
          maxWidth: 500,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box 
          sx={{ 
            bgcolor: "primary.main", 
            color: "white", 
            px: 3, 
            py: 1.5,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            PROFIT & LOSS
          </Typography>
        </Box>
        
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: "0.95rem",
                    width: 50,
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  #
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: "0.95rem",
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  Description
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: "0.95rem",
                    width: 120,
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  $
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow 
                  key={row.no}
                  sx={{ 
                    "&:hover": { bgcolor: "grey.50" },
                    borderBottom: index === rows.length - 1 ? "none" : "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <TableCell 
                    sx={{ 
                      fontSize: 14,
                      color: "text.secondary",
                      py: 2,
                    }}
                  >
                    {row.no}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontSize: 14,
                      fontWeight: row.no === 5 ? 600 : 400,
                      py: 2,
                    }}
                  >
                    {row.description}
                  </TableCell>
                  <TableCell 
                    align="right"
                    sx={{ 
                      fontSize: 14,
                      fontWeight: row.no === 5 ? 600 : 500,
                      color: row.no === 5 
                        ? difference >= 0 ? "success.main" : "error.main"
                        : "text.primary",
                      py: 2,
                    }}
                  >
                    {row.value}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
